"use client";
import React from 'react';
import styled from 'styled-components';
import BackButton from '../utils/back_button';
import { blog3 } from "../../../assets/images";
import { useNavigate } from 'react-router-dom';
import OtherBlogs from '../our_blogs/OtherBlogs';
import CTASection from "../../landing/landing_home/empower"


const Blog3 = () => {
   const navigate = useNavigate();
  const handleBackClick = () => {
     window.scrollTo(0, 0);
     navigate("/blog")
  };

  return (
    <>
       <Fullpage>
     <PageBackground>
    <BlogContainer>
      <ArticleWrapper>
        <BlogHeader
          title="Increasing Student Retention with Campus Insights"
          subtitle="Turning Engagement into Completion"
          date="JULY 23, 2025"
          author="POSTED BY CAMPUS INSIGHTS"
          onBackClick={handleBackClick}
        />
      <ImageContainer>
      <Image src={blog3} />
      </ImageContainer>
        

        <ContentSection title="The Challenge">
          <BlogContent />
          </ContentSection>
        </ArticleWrapper>
      </BlogContainer>
      <OtherBlogs currentBlogId={3} />
      </PageBackground>
      </Fullpage>
      <CTASection />
    </>
  );
};


const BlogHeader = ({
  title,
  subtitle,
  date,
  author,
  onBackClick
}) => {
  return (
    <HeaderContainer>
      <BackButtonWrapper>
        <BackButton onClick={onBackClick} />
      </BackButtonWrapper>

      <MainTitle>{title}</MainTitle>
      <Subtitle>{subtitle}</Subtitle>

      <MetadataContainer>
        <MetadataWrapper>
          <PublishDate>{date}</PublishDate>
          <AuthorInfo>{author}</AuthorInfo>
        </MetadataWrapper>
      </MetadataContainer>
    </HeaderContainer>
  );
};

const ContentSection = ({ title, children }) => {
  return (
    <SectionContainer>
      {title && <SectionTitle1>{title}</SectionTitle1>}
      <ContentWrapper>{children}</ContentWrapper>
    </SectionContainer>
  );
};

const BlogContent = () => {
  return (
    <ContentContainer>
      <ChallengeSection>
        <Paragraph>
          Nationally, colleges face declining retention rates due to a mix
          of financial stress, disengagement, unclear pathways, and support
          gaps—especially among first-gen, low-income, and underserved
          students.
        </Paragraph>
        <Paragraph>
          Campus Insights addresses this challenge head-on with AI-powered
          tools that surface insights, deliver personalized support, and
          remove friction in the student journey.
        </Paragraph>
      </ChallengeSection>

      <SolutionSection>
        <SectionTitle>💡 How Campus Insights Boosts Retention</SectionTitle>
        <FeaturesList>
          <FeatureItem>
            <FeatureTitle>1. Proactive Nudging</FeatureTitle>
            <FeatureDescription>
              Automated, personalized nudges for deadlines (e.g., financial
              aid, registration, advising) keep students on track before they
              fall behind.
            </FeatureDescription>
            <ExampleQuote>
              "Hi Jasmine! Don't forget to register for Spring classes by
              Friday. Need help choosing? Start here 👉"
            </ExampleQuote>
          </FeatureItem>

          <FeatureItem>
            <FeatureTitle>2. 24/7 Support with Smart Chat</FeatureTitle>
            <ChatImage
              src="https://api.builder.io/api/v1/image/assets/TEMP/ca23dccbb411ab33b3a1c561aae32448f781f45a?width=1526"
              alt="Smart chat interface example"
            />
            <FeatureDescription>
              Students often need help after-hours. Campus Insights provides
              accurate, context-aware answers around the clock—reducing
              dropout due to unanswered questions or confusion.
            </FeatureDescription>
          </FeatureItem>

          <FeatureItem>
            <FeatureTitle>3. Real-Time Sentiment & Risk Alerts</FeatureTitle>
            <FeatureDescription>
              By analyzing student language and engagement patterns, Campus
              Insights flags students who may be disengaged, confused, or at
              risk—so staff can intervene early.
            </FeatureDescription>
            <ExampleQuote>
              E.g., "Show me which first-year STEM students have stopped
              engaging with advising or missed deadlines."
            </ExampleQuote>
          </FeatureItem>

          <FeatureItem>
            <FeatureTitle>4. Multilingual Access</FeatureTitle>
            <FeatureDescription>
              Language shouldn't be a barrier. Campus Insights supports 100+
              languages, allowing diverse students and families to engage
              meaningfully in their education.
            </FeatureDescription>
          </FeatureItem>

          <FeatureItem>
            <FeatureTitle>5. Integrated Case Management</FeatureTitle>
            <FeatureDescription>
              Escalations from chatbot interactions can trigger follow-ups,
              referrals, or human intervention—ensuring students don't fall
              through the cracks.
            </FeatureDescription>
          </FeatureItem>

          <FeatureItem>
            <FeatureTitle>6. Data-Driven Decision-Making</FeatureTitle>
            <FeatureDescription>Staff can access insights on:</FeatureDescription>
            <InsightsList>
              <InsightItem>Top reasons students ask for help</InsightItem>
              <InsightItem>
                Funnel drop-off points (e.g., incomplete FAFSA, unregistered)
              </InsightItem>
              <InsightItem>Trends in disengagement by cohort or program</InsightItem>
            </InsightsList>
            <FeatureDescription>
              This allows targeted programming, outreach, and resource
              allocation.
            </FeatureDescription>
          </FeatureItem>
        </FeaturesList>
      </SolutionSection>

      <OutcomeSection>
        <SectionTitle>🏁 The Outcome</SectionTitle>
        <OutcomeContent>
          <OutcomeIntro>Institutions using Campus Insights can expect:</OutcomeIntro>
          <OutcomeList>
            <OutcomeItem>Increased early alert and intervention rates</OutcomeItem>
            <OutcomeItem>Higher advising and tutoring session attendance</OutcomeItem>
            <OutcomeItem>
              More students completing onboarding, FAFSA, and class
              registration
            </OutcomeItem>
            <OutcomeItem>Better student satisfaction and sense of belonging</OutcomeItem>
            <OutcomeItem>
              Improved term-to-term persistence and graduation rates
            </OutcomeItem>
          </OutcomeList>
          <ConclusionText>
            Campus Insights doesn't just answer questions—it keeps students
            connected, engaged, and moving forward.
          </ConclusionText>
        </OutcomeContent>
      </OutcomeSection>
    </ContentContainer>
  );
};

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 42px;
  width: 100%;
`;

const ChallengeSection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
`;

const SolutionSection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  width: 100%;
`;

const OutcomeSection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  width: 100%;
`;

const SectionTitle = styled.h2`
  color: #1c1b1f;
  letter-spacing: -0.32px;
  font: 700 32px/120% Inter, sans-serif;
  margin: 0;

  @media (max-width: 991px) {
    font-size: 24px;
    line-height: 120%;
  }

  @media (max-width: 640px) {
    font-size: 20px;
    line-height: 120%;
  }
`;

const Paragraph = styled.p`
  width: 100%;
  color: #64636a;
  letter-spacing: -0.08px;
  font: 400 16px/160% Inter, sans-serif;
  margin: 0;
`;

const FeaturesList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
`;

const FeatureItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
`;

const FeatureTitle = styled.h3`
  color: #64636a;
  letter-spacing: -0.2px;
  font: 400 20px/140% Inter, sans-serif;
  margin: 0;

  @media (max-width: 991px) {
    font-size: 18px;
  }

  @media (max-width: 640px) {
    font-size: 16px;
  }
`;

const FeatureDescription = styled.p`
  width: 100%;
  color: #64636a;
  letter-spacing: -0.08px;
  font: 400 16px/160% Inter, sans-serif;
  margin: 0;
`;

const ExampleQuote = styled.blockquote`
  width: 100%;
  color: #64636a;
  text-align: center;
  letter-spacing: -0.08px;
  font: italic 400 16px/160% Inter, sans-serif;
  margin: 0;
`;

const ChatImage = styled.img`
  width: 100%;
  max-width: 763px;
  height: 400px;
  border-radius: 16px;
  object-fit: cover;

  @media (max-width: 991px) {
    height: 300px;
  }

  @media (max-width: 640px) {
    height: 240px;
  }
`;

const InsightsList = styled.ul`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  margin: 0;
  padding: 0;
  list-style: none;
`;

const InsightItem = styled.li`
  width: 100%;
  color: #64636a;
  letter-spacing: -0.08px;
  font: 400 16px/160% Inter, sans-serif;
  position: relative;
  padding-left: 16px;

  &::before {
    content: "•";
    position: absolute;
    left: 0;
    color: #64636a;
  }
`;

const OutcomeContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
`;

const OutcomeIntro = styled.p`
  color: #64636a;
  letter-spacing: -0.2px;
  font: 400 20px/140% Inter, sans-serif;
  margin: 0;

  @media (max-width: 991px) {
    font-size: 18px;
  }

  @media (max-width: 640px) {
    font-size: 16px;
  }
`;

const OutcomeList = styled.ul`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  margin: 0;
  padding: 0;
  list-style: none;
`;

const OutcomeItem = styled.li`
  width: 100%;
  color: #64636a;
  letter-spacing: -0.08px;
  font: 400 16px/160% Inter, sans-serif;
  position: relative;
  padding-left: 16px;

  &::before {
    content: "•";
    position: absolute;
    left: 0;
    color: #64636a;
  }
`;

const ConclusionText = styled.p`
  color: #64636a;
  letter-spacing: -0.2px;
  font: italic 400 20px/140% Inter, sans-serif;
  margin: 0;

  @media (max-width: 991px) {
    font-size: 18px;
  }

  @media (max-width: 640px) {
    font-size: 16px;
  }
`;


const SectionContainer = styled.section`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  width: 100%;
`;

const SectionTitle1 = styled.h2`
  color: #1c1b1f;
  letter-spacing: -0.32px;
  font: 700 32px/120% Inter, sans-serif;
  margin: 0;

  @media (max-width: 991px) {
    font-size: 24px;
    line-height: 120%;
  }

  @media (max-width: 640px) {
    font-size: 20px;
    line-height: 120%;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
`;


const ImageContainer = styled.figure`
  width: 100%;
  margin: 0;
`;

const Image = styled.img`
  display: flex;
  width: 100%;
  max-width: 1008px;
  height: 346px;
  border-radius: 16px;
  object-fit: cover;

  @media (max-width: 991px) {
    height: 260px;
  }

  @media (max-width: 640px) {
    height: 200px;
  }
`;

const HeaderContainer = styled.header`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  width: 100%;
`;

const BackButtonWrapper = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 16px;
`;

const MainTitle = styled.h1`
  width: 100%;
  max-width: 967px;
  color: #1c1b1f;
  letter-spacing: -0.48px;
  font: 700 48px/120% Inter, sans-serif;
  margin: 0;

  @media (max-width: 991px) {
    font-size: 36px;
    line-height: 120%;
  }

  @media (max-width: 640px) {
    font-size: 28px;
    line-height: 120%;
  }
`;

const Subtitle = styled.h2`
  width: 100%;
  max-width: 967px;
  color: #1c1b1f;
  letter-spacing: -0.32px;
  font: 700 32px/120% Inter, sans-serif;
  margin: 0;

  @media (max-width: 991px) {
    font-size: 24px;
    line-height: 120%;
  }

  @media (max-width: 640px) {
    font-size: 20px;
    line-height: 120%;
  }
`;

const MetadataContainer = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 16px;
`;

const MetadataWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;
const PublishDate = styled.time`
  color: #64636a;
  letter-spacing: 2px;
  text-transform: uppercase;
  font: 400 16px/120% Inter, sans-serif;

  @media (max-width: 640px) {
    font-size: 14px;
  }
`;

const AuthorInfo = styled.span`
  color: #64636a;
  letter-spacing: 2px;
  text-transform: uppercase;
  font: 400 16px/120% Inter, sans-serif;

  @media (max-width: 640px) {
    font-size: 14px;
  }
`;

const BlogContainer = styled.main`
  display: flex;
  width: 1120px;
  padding: 56px;
  flex-direction: column;
  align-items: flex-start;
  gap: 64px;
  border-radius: 24px;
  margin: 0 auto;
  max-width: 100%;
  box-sizing: border-box;
  background-color: #fff;

  @media (max-width: 991px) {
    width: 100%;
    padding: 40px 32px;
    gap: 48px;
  }

  @media (max-width: 640px) {
    padding: 24px 16px;
    gap: 32px;
  }
`;

const ArticleWrapper = styled.article`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 32px;
  width: 100%;
`;

const PageBackground = styled.div`
  height: 100%;
  width: 100%;
  background: linear-gradient(to bottom, #5e2bff 0px, #5e2bff 500px, #F5F5F9 100px, #F5F5F9 100%);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 200px 0;
    flex-direction: column;

  @media (max-width: 640px) {
    padding: 40px 0;
    background: linear-gradient(to bottom, #5e2bff 0px, #5e2bff 100px, #ffffff 100px, #ffffff 100%);
  }
`;

const Fullpage = styled.div`
  height: 100%;
  width: 100%;
//   background-color: #e20707ff;
  display: flex;
  justify-content: center;
  align-items: flex-start;
 

  @media (max-width: 640px) {
    padding: 40px 0;
   
  }
`;


export default Blog3;
